/**
 * Role → content cap · SINGLE SOURCE OF TRUTH for the cohort/tariff tiers
 * (slot_role_management · 10 JUL 2026 · Genie).
 *
 * Every access tier is just a cap on the highest sprint ORD a user may receive
 * content for. Adding or re-pricing a tier = editing ONE row in ROLE_TIERS; the
 * server gate (`entitlement.ts`), the client UX (`access.ts`), the admin panel
 * and the DB-role coercion all read from this table — so there is no scattered
 * `=== 'trial'` / hardcoded `TRIAL_MAX` logic to keep in sync.
 *
 * Lives in shared/ so the client, vitest (Node) and the edge content/quiz
 * functions (mirrored via sync-edge-shared) use identical rules — no drift in a
 * security boundary. Pure data + pure helpers · no DOM / Deno / browser globals.
 *
 * ⚠ The `role` keys here MUST stay in lock-step with:
 *   · UserRoleSchema (shared/schemas/user.schema.ts · the zod enum)
 *   · the DB `profiles_role_check` CHECK constraint (migration).
 * Changing a key is a DB-constraint change — do all three together.
 */

import type { UserRole } from '../schemas';

export interface RoleTier {
  /** Canonical key · matches UserRoleSchema + the DB CHECK constraint. */
  role: UserRole;
  /** Highest sprint ord this tier may receive content for. Infinity = uncapped. */
  maxOrd: number;
  /** RU display label · admin dropdown + profile badge. */
  label: string;
  /** One-line description of the cohort (admin UI helper text). */
  note: string;
  /** Paid purchase? Gates non-sprint banks (mock exams) + suppresses free-tier upsell. */
  paid: boolean;
  /** Ascending privilege order · admin dropdown + migration ranking. */
  rank: number;
}

/**
 * The cohort table (Genie 10 JUL 2026). Каждый тариф = cap по последнему спринту.
 *
 *   free       → S01        (лид-магнит · не оплачен)
 *   trial      → S01–S04     (демо-раунд · не оплачен)
 *   start      → S01–S07     (оплачен)
 *   manualplus → S01–S13     (оплачен)
 *   pro        → S01–S19     (оплачен · весь текущий курс = 19 спринтов)
 *   admin      → всё         (dev unlock-all)
 *
 * `pro` == the whole shipped course today (19 sprints). When sprint S20 lands,
 * bump `pro.maxOrd` here (or introduce a new top tier) — nothing else changes.
 */
export const ROLE_TIERS: readonly RoleTier[] = [
  { role: 'free', maxOrd: 1, label: 'Free · лид-магнит', note: 'S01 · бесплатный лид-магнит', paid: false, rank: 0 },
  { role: 'trial', maxOrd: 4, label: 'Trial · демо', note: 'S01–S04 · демо-раунд, без оплаты', paid: false, rank: 1 },
  { role: 'start', maxOrd: 7, label: 'Старт', note: 'S01–S07 · оплачен', paid: true, rank: 2 },
  { role: 'manualplus', maxOrd: 13, label: 'Manual+', note: 'S01–S13 · оплачен', paid: true, rank: 3 },
  { role: 'pro', maxOrd: 19, label: 'Pro', note: 'S01–S19 · весь курс, оплачен', paid: true, rank: 4 },
  { role: 'admin', maxOrd: Infinity, label: 'Admin', note: 'всё · dev unlock-all', paid: true, rank: 5 },
] as const;

const TIER_BY_ROLE: Record<UserRole, RoleTier> = ROLE_TIERS.reduce(
  (acc, tier) => {
    acc[tier.role] = tier;
    return acc;
  },
  {} as Record<UserRole, RoleTier>,
);

/** Least-privilege fallback for an absent / unknown role. */
export const DEFAULT_ROLE: UserRole = 'trial';

/** The tier record for a role · falls back to the DEFAULT_ROLE tier if unknown. */
export function tierOf(role: UserRole): RoleTier {
  return TIER_BY_ROLE[role] ?? TIER_BY_ROLE[DEFAULT_ROLE];
}

/** Highest sprint ord `role` may receive content for (Infinity = admin/uncapped). */
export function maxOrdForRole(role: UserRole): number {
  return tierOf(role).maxOrd;
}

/**
 * True when the tier is a paid purchase (start / manualplus / pro / admin).
 * Gates non-sprint banks (mock exams) and suppresses the free/trial upsell.
 */
export function isPaidRole(role: UserRole): boolean {
  return tierOf(role).paid;
}

/** RU display label for a role. */
export function roleLabel(role: UserRole): string {
  return tierOf(role).label;
}

/** All role keys, ascending privilege (admin last) · admin dropdown / migration. */
export const ROLE_KEYS: readonly UserRole[] = ROLE_TIERS.map((tier) => tier.role);

/**
 * The next PURCHASABLE tier that unlocks more sprints than `role` — i.e. the
 * cheapest paid tier with a strictly higher cap. Powers the upsell CTA/copy
 * («перейди на Старт/Manual+/Pro»). `admin` is excluded (not for sale). Returns
 * null for `pro`/`admin` (nothing higher to buy). free/trial → start.
 */
export function nextPaidTierFor(role: UserRole): RoleTier | null {
  const currentMax = maxOrdForRole(role);
  const higher = ROLE_TIERS.filter(
    (t) => t.paid && t.role !== 'admin' && t.maxOrd > currentMax,
  ).sort((a, b) => a.maxOrd - b.maxOrd);
  return higher[0] ?? null;
}

/**
 * Coerce a raw DB/JWT value into a valid UserRole.
 *   · a known key → itself.
 *   · legacy `'full'` (pre-10-JUL tier · all sprints) → `'pro'` (top paid tier),
 *     so any straggler row/token UPGRADES rather than silently downgrading.
 *   · anything else → DEFAULT_ROLE (least privilege).
 * Centralizes the coercion the server (me.ts) and edge fns (content/quiz) do.
 */
export function coerceRole(raw: unknown): UserRole {
  if (typeof raw === 'string') {
    if (raw in TIER_BY_ROLE) return raw as UserRole;
    if (raw === 'full') return 'pro'; // legacy alias · never downgrade a paid user
  }
  return DEFAULT_ROLE;
}
